import * as Phaser from 'phaser';
import type { PaintedSceneAssetSpec, NatureConfig, NatureElement } from './sceneAssetSpec.schema';
import { samplePerspectiveScale } from './sceneAssetSpec.schema';
import type { ParallaxLayerManager } from './ParallaxLayerManager';
import type { WalkablePolygon } from './WalkablePolygon';
import type { SceneLayer } from './sceneAssetSpec.schema';

function fnv1aHash(str: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function mulberry32(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface SeededRNG {
  next(): number;
  range(min: number, max: number): number;
  intRange(min: number, max: number): number;
}

function createRNG(seed: number): SeededRNG {
  const raw = mulberry32(seed);
  return {
    next: raw,
    range(min: number, max: number): number {
      return min + raw() * (max - min);
    },
    intRange(min: number, max: number): number {
      return min + Math.floor(raw() * (max - min + 1));
    },
  };
}

const PLACEHOLDER_DEFS: Record<string, { w: number; h: number; draw: (g: Phaser.GameObjects.Graphics) => void }> = {
  wildflower: {
    w: 8, h: 12,
    draw(g) {
      g.fillStyle(0x4a7c59, 1); g.fillRect(3, 4, 2, 8);
      g.fillStyle(0xe8a0b0, 1); g.fillCircle(4, 3, 3);
    },
  },
  rock: {
    w: 10, h: 6,
    draw(g) {
      g.fillStyle(0x8a8a8a, 1); g.fillRoundedRect(0, 0, 10, 6, 2);
    },
  },
  grass: {
    w: 4, h: 16,
    draw(g) {
      g.fillStyle(0x5a9a4a, 1); g.fillRect(0, 0, 4, 16);
    },
  },
};

function guessPlaceholderType(spriteKey: string): string {
  const k = spriteKey.toLowerCase();
  if (k.includes('rock') || k.includes('stone')) return 'rock';
  if (k.includes('grass') || k.includes('tuft')) return 'grass';
  return 'wildflower';
}

const MAX_ATTEMPTS = 20;

export class NatureScatterSystem {
  private readonly scene: Phaser.Scene;
  private sprites: Phaser.GameObjects.Image[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  scatter(
    spec: PaintedSceneAssetSpec,
    polygon: WalkablePolygon,
    layerManager: ParallaxLayerManager,
  ): void {
    const nature = spec.nature;
    if (!nature) return;

    const seed = nature.seed ?? fnv1aHash(spec.key);
    const rng = createRNG(seed);
    const budget = nature.maxInstances;
    const w = this.scene.scale.width;
    const h = this.scene.scale.height;
    const walkableArea = polygon.estimateArea();
    let placed = 0;

    for (const element of nature.elements) {
      if (placed >= budget) break;
      const count = Math.min(
        Math.floor(element.baseDensity * nature.densityScale * walkableArea),
        budget - placed,
      );

      for (let i = 0; i < count; i++) {
        const pos = this.samplePosition(rng, polygon, nature);
        if (!pos) continue;

        const bracket = rng.intRange(element.depthBracketRange[0], element.depthBracketRange[1]);
        const jitter = rng.range(element.scaleJitter[0], element.scaleJitter[1]);
        const scale = samplePerspectiveScale(pos.ny, spec.perspective) * element.baseScale * jitter;
        const flipX = element.randomFlipX && rng.next() > 0.5;

        this.ensurePlaceholderTexture(element.spriteKey);
        const sprite = this.scene.add.image(pos.nx * w, pos.ny * h, element.spriteKey);
        sprite.setScale(scale);
        sprite.setFlipX(flipX);
        sprite.setOrigin(0.5, 1);

        if (element.tintJitter !== undefined && element.tintJitter > 0) {
          const dr = rng.intRange(-element.tintJitter, element.tintJitter);
          const dg = rng.intRange(-element.tintJitter, element.tintJitter);
          const db = rng.intRange(-element.tintJitter, element.tintJitter);
          const tint = ((0xff + dr) << 16) | ((0xff + dg) << 8) | (0xff + db);
          sprite.setTint(tint);
        }

        this.sprites.push(sprite);

        const depthOffset = rng.intRange(1, 90);
        const layerSpec: SceneLayer = {
          id: `nature-${element.spriteKey}-${placed}`,
          depth: depthOffset,
          alpha: 1,
          parallax: 1,
          organicMotion: element.organicMotion.length > 0 ? [...element.organicMotion] : undefined,
          depthBracket: bracket,
        };

        layerManager.addExternalLayer(layerSpec, sprite);
        placed++;
      }
    }
  }

  destroy(): void {
    this.sprites = [];
  }

  private samplePosition(
    rng: SeededRNG,
    polygon: WalkablePolygon,
    nature: NatureConfig,
  ): { nx: number; ny: number } | null {
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      const nx = rng.next();
      const ny = rng.next();
      if (!polygon.contains(nx, ny)) continue;
      if (this.insideExclusionZone(nx, ny, nature)) continue;
      return { nx, ny };
    }
    return null;
  }

  private insideExclusionZone(nx: number, ny: number, nature: NatureConfig): boolean {
    if (!nature.exclusionZones) return false;
    for (const zone of nature.exclusionZones) {
      if (nx >= zone.x && nx <= zone.x + zone.w && ny >= zone.y && ny <= zone.y + zone.h) {
        return true;
      }
    }
    return false;
  }

  private ensurePlaceholderTexture(spriteKey: string): void {
    if (this.scene.textures.exists(spriteKey)) return;
    const type = guessPlaceholderType(spriteKey);
    const def = PLACEHOLDER_DEFS[type] ?? PLACEHOLDER_DEFS['wildflower'];
    const g = this.scene.make.graphics({ x: 0, y: 0 }, false);
    def.draw(g);
    g.generateTexture(spriteKey, def.w, def.h);
    g.destroy();
  }
}
